import { Card, Flex, Tooltip, Typography } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined, MinusOutlined, RetweetOutlined } from "@ant-design/icons";
import { formatAmount, etiquetaComparativa, type DashboardPeriod } from "../../utils";
import { estadoDeWidget, WidgetPlaceholder } from "../widget-state";
import type { WidgetDataProps } from "../../types";

const { Text } = Typography;



type Stat = { count: number; amount: number };


// ── Delta badge ───────────────────────────────────────────────────────────────
const DeltaBadge = ({ actual, anterior }: { actual: number; anterior: number }) => {
  if (anterior === 0 && actual === 0) return null;
  const diff = actual - anterior;
  if (diff === 0) return (
    <span style={{ fontSize: 11, color: "rgba(0,0,0,0.3)", display: "inline-flex", alignItems: "center", gap: 2 }}>
      <MinusOutlined style={{ fontSize: 9 }} /> igual
    </span>
  );
  const up    = diff > 0;
  const color = up ? "#15744b" : "#cf1322";
  const Icon  = up ? ArrowUpOutlined : ArrowDownOutlined;
  const pct   = anterior > 0 ? Math.round((diff / anterior) * 100) : null;
  const label = pct !== null ? `${up ? "+" : ""}${pct}%` : `${up ? "+" : ""}${diff}`;
  return (
    <span style={{ fontSize: 11, color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}>
      <Icon style={{ fontSize: 9 }} /> {label}
    </span>
  );
};

// ── Mini bars (actual vs anterior) ────────────────────────────────────────────
const MiniBar = ({ actual, anterior, loading }: { actual: number; anterior: number; loading: boolean }) => {
  const max      = Math.max(actual, anterior, 1);
  const wActual  = Math.max((actual   / max) * 100, actual   > 0 ? 4 : 0);
  const wAnterior = Math.max((anterior / max) * 100, anterior > 0 ? 4 : 0);
  return (
    <div style={{ opacity: loading ? 0.3 : 1 }}>
      <div style={{ height: 6, background: "rgba(0,0,0,0.06)", borderRadius: 3, overflow: "hidden", marginBottom: 3 }}>
        <div style={{ width: `${wActual}%`, height: "100%", background: "#15744b", borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
      <div style={{ height: 6, background: "rgba(0,0,0,0.06)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${wAnterior}%`, height: "100%", background: "rgba(0,0,0,0.15)", borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );
};

// ── Section block ─────────────────────────────────────────────────────────────
const Section = ({ label, actual, anterior, loading }: { label: string; actual: Stat; anterior: Stat; loading: boolean }) => (
  <div>
    <Text style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.35)", letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 2 }}>
      {label}
    </Text>
    <Flex align="baseline" gap={8} style={{ marginBottom: 2 }}>
      <Text strong style={{ fontSize: 22, lineHeight: 1, opacity: loading ? 0.3 : 1 }}>
        {actual.count}
      </Text>
      {!loading && <DeltaBadge actual={actual.count} anterior={anterior.count} />}
    </Flex>
    <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4, opacity: loading ? 0.3 : 1 }}>
      {formatAmount(actual.amount)} · vs {formatAmount(anterior.amount)} ant. ({anterior.count})
    </Text>
    <MiniBar actual={actual.count} anterior={anterior.count} loading={loading} />
  </div>
);

// ── ComparativaChips ──────────────────────────────────────────────────────────
type Par = { actual: Stat | null; anterior: Stat | null };
type Datos = { quotes: Par; orders: Par };

const CERO: Stat = { count: 0, amount: 0 };

export const ComparativaChips = ({
  period,
  height,
  data,
  estado,
  error,
  nuncaCalculado,
  recargar,
}: { period: DashboardPeriod; height?: number } & WidgetDataProps<Datos>) => {
  // `historic` no tiene período anterior contra el cual comparar: el widget no aplica.
  if (period === "historic") return null;

  const quotes = {
    actual: data?.quotes?.actual ?? CERO,
    anterior: data?.quotes?.anterior ?? CERO,
  };
  const orders = {
    actual: data?.orders?.actual ?? CERO,
    anterior: data?.orders?.anterior ?? CERO,
  };
  const hayDatos =
    quotes.actual.count > 0 ||
    quotes.anterior.count > 0 ||
    orders.actual.count > 0 ||
    orders.anterior.count > 0;
  const status = estadoDeWidget(estado, error, nuncaCalculado, hayDatos, recargar);
  const loading = estado === "cargando";

  const CARD_HEAD = 38;
  const bodyH     = (height ?? 240) - CARD_HEAD;

  return (
    <Card
      size="small"
      style={{ height: height ?? 240 }}
      styles={{ body: { padding: "10px 16px 12px", height: bodyH, display: "flex", flexDirection: "column", justifyContent: "space-between" } }}
      title={
        <Flex align="center" gap={8}>
          <RetweetOutlined style={{ fontSize: 14 }} />
          <span style={{ fontWeight: 600 }}>Comparativa de período</span>
          <Tooltip
            title="Compara los presupuestos y pedidos creados en el período actual contra el mismo tramo del anterior: si van 18 días de mes, se compara contra los primeros 18 del mes pasado. Una flecha verde indica crecimiento."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 240 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
      extra={
        <Text type="secondary" style={{ fontSize: 11 }}>
          {etiquetaComparativa(period)}
        </Text>
      }
    >
      {status.tipo !== "ok" ? (
        <WidgetPlaceholder status={status} height={(height ?? 240) - 60} />
      ) : (
        <>
      <Section label="Presupuestos" actual={quotes.actual} anterior={quotes.anterior} loading={loading} />
      <div style={{ height: 1, background: "#f0f0f0" }} />
      <Section label="Pedidos" actual={orders.actual} anterior={orders.anterior} loading={loading} />

      {/* Shared legend */}
      <Flex justify="space-between">
        <Flex align="center" gap={4}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: "#15744b", display: "inline-block" }} />
          <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.35)" }}>Actual</Text>
        </Flex>
        <Flex align="center" gap={4}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: "rgba(0,0,0,0.15)", display: "inline-block" }} />
          <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.35)" }}>Anterior</Text>
        </Flex>
      </Flex>
            </>
      )}
    </Card>
  );
};
